import { Loader2, MessageCircleQuestion, Search, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { deleteAdminTickets, listAdminTickets } from '@/api/admin';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE, useDebouncedValue } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';
import AdminTicketModal from '@/components/admin/tickets/AdminTicketModal';
import ConfirmDeleteTicketsModal from '@/components/admin/tickets/ConfirmDeleteTicketsModal';
import { errorMessage, INBOX_TABS, inboxLabel, inboxPillStyle } from '@/components/admin/tickets/ticketInbox';

const formatWhen = (iso) => (iso ? String(iso).replace('T', ' ').slice(0, 16) : '');
const lastMessage = (t) => t.messages?.[t.messages.length - 1];

/**
 * The admin support inbox, 12DPC-style: tabs for New / Continued Support / Waiting On Member /
 * Resolved, search by subject, username or email, and delete per ticket or for every ticket ticked
 * on the page. Clicking a ticket opens its conversation.
 */
const TicketsSubpage = () => {
  const [page, setPage] = useState(1);
  const [inbox, setInbox] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dates, setDates] = useState(EMPTY_DATE_RANGE);
  const appliedDates = useDebouncedValue(dates);
  const [data, setData] = useState({ items: [], page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [open, setOpen] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // ticket ids awaiting confirmation
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await listAdminTickets(page, inbox, search, dateRangeParams(appliedDates)));
    } catch (err) {
      toast.error(`Failed to load tickets: ${errorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, [page, inbox, search, appliedDates]);

  useEffect(() => {
    load();
  }, [load]);

  // A different page, tab, search or date range shows different rows: drop the ticks.
  // biome-ignore lint/correctness/useExhaustiveDependencies: page/inbox/search/dates are the triggers
  useEffect(() => {
    setSelected(new Set());
  }, [page, inbox, search, appliedDates]);

  const rows = data.items || [];
  const allTicked = rows.length > 0 && rows.every((t) => selected.has(t.ticket_id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => setSelected(allTicked ? new Set() : new Set(rows.map((t) => t.ticket_id)));

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const r = await deleteAdminTickets(pendingDelete);
      toast.success(`Deleted ${r.deleted} ticket${r.deleted === 1 ? '' : 's'}`);
      setPendingDelete(null);
      setOpen(null);
      setSelected(new Set());
      // Deleting the last rows of a page steps back a page instead of showing an empty one.
      if (page > 1 && r.deleted >= rows.length) setPage(page - 1);
      else load();
    } catch (err) {
      toast.error(`Delete failed: ${errorMessage(err)}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="tickets-subpage">
      <div className="sc-glass rounded-2xl p-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="font-display font-bold text-xl flex items-center gap-2">
              <MessageCircleQuestion className="w-5 h-5 text-[#FFC20E]" />
              Support Inbox
            </div>
            <p className="text-xs text-[var(--sc-text-dim)] mt-1">
              New tickets have no reply yet; Continued Support means the member wrote back. Click a ticket to read the conversation and reply.
            </p>
          </div>
          <form onSubmit={applySearch} className="flex items-center gap-2 w-full md:w-auto" data-testid="tickets-search-form">
            <div className="relative flex-1 md:w-[280px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--sc-text-mute)]" />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search username, email or subject"
                maxLength={100}
                className="sc-input"
                style={{ paddingLeft: 32 }}
                data-testid="tickets-search"
              />
            </div>
            <button type="submit" className="sc-btn-gold" style={{ padding: '9px 14px', fontSize: 11 }} data-testid="tickets-search-btn">
              Search
            </button>
            {search ? (
              <button
                type="button"
                onClick={clearSearch}
                className="sc-btn-ghost"
                style={{ padding: '9px 12px', fontSize: 11 }}
                data-testid="tickets-search-clear"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            ) : null}
          </form>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1" role="tablist" data-testid="tickets-tabs">
          {INBOX_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={inbox === tab.value}
              onClick={() => {
                setInbox(tab.value);
                setPage(1);
              }}
              className={`shrink-0 px-3 py-2 rounded-lg border text-[11px] uppercase tracking-[0.16em] font-semibold transition ${
                inbox === tab.value
                  ? 'border-[rgba(255,182,18,0.6)] bg-[rgba(255,182,18,0.12)] text-[#FFC20E]'
                  : 'border-[rgba(255,255,255,0.10)] text-[var(--sc-text-mute)] hover:text-[var(--sc-text)]'
              }`}
              data-testid={`tickets-tab-${tab.value}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <AdminDateRange
            wrap
            dateFrom={dates.date_from}
            dateTo={dates.date_to}
            onChange={(range) => {
              setDates(range);
              setPage(1);
            }}
            fromLabel="Last Activity From"
            toLabel="Last Activity To"
            testIdPrefix="tickets-date"
          />
        </div>
      </div>

      <div className="sc-glass rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">
            {data.total ?? 0} {inboxLabel(inbox)}
            {search ? ` matching “${search}”` : ''}
          </div>
          <button
            type="button"
            onClick={() => setPendingDelete([...selected])}
            disabled={selected.size === 0}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#FF6B6B] border border-[rgba(255,107,107,0.45)] hover:bg-[rgba(255,107,107,0.08)] disabled:opacity-40"
            data-testid="tickets-delete-selected"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete Selected{selected.size > 0 ? ` (${selected.size})` : ''}
          </button>
        </div>

        <div className="sc-admin-table-wrap">
          <table className="sc-admin-table" data-testid="tickets-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    className="sc-checkbox"
                    aria-label="Select every ticket on this page"
                    checked={allTicked}
                    onChange={toggleAll}
                    data-testid="tickets-select-all"
                  />
                </th>
                <th>Subject</th>
                <th>Member</th>
                <th>Last Activity</th>
                <th>Status</th>
                <th style={{ width: 48 }} aria-label="Delete" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FFB612]" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-[var(--sc-text-mute)]">
                    {search ? 'No tickets match that search.' : 'No tickets here.'}
                  </td>
                </tr>
              ) : (
                rows.map((t) => {
                  const last = lastMessage(t);
                  return (
                    <tr key={t.ticket_id} onClick={() => setOpen(t)} className="cursor-pointer" data-testid={`ticket-row-${t.ticket_id}`}>
                      <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="sc-checkbox"
                          aria-label={`Select ticket ${t.subject}`}
                          checked={selected.has(t.ticket_id)}
                          onChange={() => toggle(t.ticket_id)}
                          data-testid={`ticket-select-${t.ticket_id}`}
                        />
                      </td>
                      <td>
                        <div className="font-semibold text-sm">{t.subject}</div>
                        <div className="text-xs text-[var(--sc-text-mute)] truncate max-w-[420px]">
                          {last?.sender_role === 'admin' ? 'Support: ' : ''}
                          {last?.body ?? t.message}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">{t.username || t.user_id}</div>
                        <div className="text-[11px] text-[var(--sc-text-mute)]">{t.email}</div>
                      </td>
                      <td className="text-xs text-[var(--sc-text-mute)] whitespace-nowrap">{formatWhen(t.last_message_at)}</td>
                      <td>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.14em] uppercase whitespace-nowrap"
                          style={inboxPillStyle(t.inbox_status)}
                        >
                          {inboxLabel(t.inbox_status)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete([t.ticket_id]);
                          }}
                          className="p-1.5 rounded-md text-[var(--sc-text-mute)] hover:text-[#FF6B6B] hover:bg-[rgba(255,107,107,0.08)]"
                          aria-label={`Delete ticket ${t.subject}`}
                          data-testid={`ticket-delete-${t.ticket_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <AdminPagination page={data.page} totalPages={data.total_pages} total={data.total} onChange={(p) => setPage(p)} />
      </div>

      <AdminTicketModal ticket={open} onClose={() => setOpen(null)} onChanged={load} onDelete={(t) => setPendingDelete([t.ticket_id])} />
      {pendingDelete ? (
        <ConfirmDeleteTicketsModal count={pendingDelete.length} busy={deleting} onCancel={() => setPendingDelete(null)} onConfirm={confirmDelete} />
      ) : null}
    </div>
  );
};

export default TicketsSubpage;
